import { useState } from 'react';

import {
  MISTRAL_DIRECT_API_OCR_URL,
  MISTRAL_HOSTING_OPTIONS,
  MistralHostings,
  OCR_CAPABILITY_OPTIONS,
  OCR_MODE_OPTIONS,
  OcrAuthModes,
  OcrModes,
  OcrProviderKinds,
  type CreateOcrProviderRequest,
  type MistralHosting,
  type OcrAuthMode,
  type OcrMode,
  type OcrProviderKind,
  type OcrProviderResponse,
  type UpdateOcrProviderRequest,
} from '../api';
import { useLicensePlans } from '../hooks/useLicensePlans';
import { useOcrProviderModels } from '../hooks/useOcrProviders';

type FormMode = 'create' | 'edit';

interface OcrProviderFormProps {
  mode: FormMode;
  provider?: OcrProviderResponse;
  busy: boolean;
  onClose: () => void;
  onCreate?: (request: CreateOcrProviderRequest) => void;
  onUpdate?: (request: UpdateOcrProviderRequest) => void;
}

/** Parses a numeric text input into an optional non-negative integer (blank → null). */
function parseLimit(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}

/**
 * Create/edit modal for a platform OCR provider. Fields are conditional on the provider kind:
 * Azure Document Intelligence has no model/region/key fields here (it is configured per-license via
 * the «Recursos DI» surface and used as the default), while Mistral exposes the full Foundry config.
 */
export function OcrProviderForm({
  mode,
  provider,
  busy,
  onClose,
  onCreate,
  onUpdate,
}: OcrProviderFormProps): JSX.Element {
  const { data: plans } = useLicensePlans();

  const [kind] = useState<OcrProviderKind>(provider?.kind ?? OcrProviderKinds.Mistral);
  const [displayName, setDisplayName] = useState(provider?.displayName ?? '');
  const [modelId, setModelId] = useState(provider?.modelId ?? '');
  const [endpoint, setEndpoint] = useState(provider?.endpoint ?? '');
  const [region, setRegion] = useState(provider?.region ?? '');
  const [subscriptionId, setSubscriptionId] = useState(provider?.subscriptionId ?? '');
  const [authMode, setAuthMode] = useState<OcrAuthMode>(provider?.authMode ?? OcrAuthModes.ApiKey);
  const [apiKey, setApiKey] = useState('');
  const [capabilities, setCapabilities] = useState<string[]>(
    provider?.capabilities ?? ['Text', 'Tables'],
  );
  const [monthlyCalls, setMonthlyCalls] = useState(
    provider?.usageLimitMonthlyCalls != null ? String(provider.usageLimitMonthlyCalls) : '',
  );
  const [monthlyPages, setMonthlyPages] = useState(
    provider?.usageLimitMonthlyPages != null ? String(provider.usageLimitMonthlyPages) : '',
  );
  const [assignedLicenses, setAssignedLicenses] = useState<string[]>(
    provider?.assignedLicenses ?? [],
  );
  const [isDiscoveryDefault, setIsDiscoveryDefault] = useState(provider?.isDiscoveryDefault ?? false);
  const [providerMode, setProviderMode] = useState<OcrMode>(provider?.mode ?? OcrModes.Ocr);
  const [hosting, setHosting] = useState<MistralHosting>(provider?.hosting ?? MistralHostings.AzureFoundry);

  // Picking the direct Mistral API prefills its base URL (when empty) and forces ApiKey auth — there is
  // no Azure Managed Identity for api.mistral.ai.
  const onHostingChange = (next: MistralHosting): void => {
    setHosting(next);
    if (next === MistralHostings.MistralApi) {
      if (endpoint.trim() === '') setEndpoint(MISTRAL_DIRECT_API_OCR_URL);
      setAuthMode(OcrAuthModes.ApiKey);
    }
  };

  // Model suggestions are kind-specific; the input remains free text so a new model can be registered.
  const { data: modelCatalog } = useOcrProviderModels(kind);
  const isMistral = kind === OcrProviderKinds.Mistral;
  const usesApiKey = authMode === OcrAuthModes.ApiKey;

  const toggleLicense = (l: string): void => {
    setAssignedLicenses((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));
  };

  const toggleCapability = (c: string): void => {
    setCapabilities((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (mode === 'create') {
      onCreate?.({
        kind,
        displayName,
        modelId: isMistral ? modelId : null,
        endpoint,
        region: region.trim() !== '' ? region.trim() : null,
        subscriptionId: subscriptionId.trim() !== '' ? subscriptionId.trim() : null,
        authMode,
        apiKeyPlaintext: usesApiKey ? apiKey : null,
        capabilities,
        usageLimitMonthlyCalls: parseLimit(monthlyCalls),
        usageLimitMonthlyPages: parseLimit(monthlyPages),
        assignedLicenses,
        isDiscoveryDefault,
        mode: providerMode,
        hosting,
      });
      return;
    }

    // Edit: re-send the authoritative resource state (the backend treats present values as new state).
    const request: UpdateOcrProviderRequest = {
      displayName,
      modelId: isMistral ? modelId : null,
      endpoint,
      region: region.trim() !== '' ? region.trim() : null,
      subscriptionId: subscriptionId.trim() !== '' ? subscriptionId.trim() : null,
      authMode,
      capabilities,
      usageLimitMonthlyCalls: parseLimit(monthlyCalls),
      usageLimitMonthlyPages: parseLimit(monthlyPages),
      assignedLicenses,
      isDiscoveryDefault,
      mode: providerMode,
      hosting,
    };
    // Blank key on edit = keep the current credential (no rotation).
    if (usesApiKey && apiKey.trim() !== '') request.apiKeyPlaintext = apiKey;
    onUpdate?.(request);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="opf-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200/70 bg-white p-4 shadow-xl sm:p-6"
      >
        <h2 id="opf-title" className="text-lg font-semibold text-slate-900">
          {mode === 'create' ? 'Añadir proveedor OCR' : 'Editar proveedor OCR'}
        </h2>

        <div className="mt-5 space-y-4">
          {/* Kind — chosen at creation only; switching backend on an existing resource is not supported. */}
          <div>
            <label className="label" htmlFor="opf-kind">
              Tipo de proveedor
            </label>
            <select id="opf-kind" className="input bg-slate-50" value={kind} disabled aria-readonly>
              <option value={OcrProviderKinds.Mistral}>Mistral OCR / Document AI</option>
              <option value={OcrProviderKinds.AzureDocumentIntelligence}>
                Azure Document Intelligence
              </option>
            </select>
            {mode === 'create' ? (
              <p className="mt-1 text-xs text-slate-500">
                El tipo se fija al crear. Azure Document Intelligence se configura por licencia en
                «Recursos DI» y actúa como motor por defecto.
              </p>
            ) : null}
          </div>

          <div>
            <label className="label" htmlFor="opf-name">
              Nombre
            </label>
            <input
              id="opf-name"
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={busy}
              required
            />
          </div>

          {isMistral ? (
            <>
              <div>
                <label className="label" htmlFor="opf-hosting">
                  Hosting
                </label>
                <select
                  id="opf-hosting"
                  className="input"
                  value={hosting}
                  onChange={(e) => onHostingChange(e.target.value as MistralHosting)}
                  disabled={busy}
                >
                  {MISTRAL_HOSTING_OPTIONS.map((h) => (
                    <option key={h.value} value={h.value}>
                      {h.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  «API directa de Mistral» evita Azure Foundry (sin sus 503), procesa en la UE y usa tu API
                  key de Mistral (auth por API key). Modelo sugerido: <code className="font-mono">mistral-ocr-latest</code>.
                </p>
              </div>

              <div>
                <label className="label" htmlFor="opf-model">
                  Modelo
                </label>
                <input
                  id="opf-model"
                  className="input"
                  list="opf-model-options"
                  value={modelId}
                  onChange={(e) => setModelId(e.target.value)}
                  disabled={busy}
                  placeholder="mistral-document-ai-2505"
                  required
                />
                <datalist id="opf-model-options">
                  {(modelCatalog?.models ?? []).map((m) => (
                    <option key={m.modelId} value={m.modelId}>
                      {m.displayName}
                    </option>
                  ))}
                </datalist>
                <p className="mt-1 text-xs text-slate-500">
                  Puedes escribir un modelo nuevo aunque no esté en la lista.
                </p>
              </div>

              <div>
                <label className="label" htmlFor="opf-endpoint">
                  Endpoint OCR (URL completa)
                </label>
                <input
                  id="opf-endpoint"
                  className="input"
                  placeholder={
                    hosting === MistralHostings.MistralApi
                      ? 'https://api.mistral.ai/v1/ocr'
                      : 'https://{proyecto}.{region}.models.ai.azure.com/providers/mistral/azure/ocr'
                  }
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  disabled={busy}
                  required
                />
                <p className="mt-1 text-xs text-slate-500">
                  Se usa <strong>tal cual</strong>: pega la URL COMPLETA del endpoint OCR (la misma que te
                  funciona en Postman). El sistema no le añade ningún path.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="opf-region">
                    Región
                  </label>
                  <input
                    id="opf-region"
                    className="input"
                    placeholder="francecentral"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="opf-sub">
                    Subscription ID
                  </label>
                  <input
                    id="opf-sub"
                    className="input"
                    value={subscriptionId}
                    onChange={(e) => setSubscriptionId(e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>
            </>
          ) : (
            <div>
              <label className="label" htmlFor="opf-endpoint">
                Endpoint
              </label>
              <input
                id="opf-endpoint"
                className="input"
                placeholder="https://…"
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                disabled={busy}
                required
              />
            </div>
          )}

          {/* Auth mode — ApiKey shows a credential field, Managed Identity uses Entra ID (no key). */}
          <div>
            <p className="label mb-1">Autenticación</p>
            <div className="flex gap-4">
              {[OcrAuthModes.ApiKey, OcrAuthModes.ManagedIdentity].map((m) => (
                <label key={m} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="radio"
                    name="opf-auth"
                    checked={authMode === m}
                    onChange={() => setAuthMode(m)}
                    disabled={busy}
                    className="h-4 w-4 border-slate-300 text-primary focus:ring-primary"
                  />
                  {m === OcrAuthModes.ApiKey ? 'API key' : 'Managed Identity'}
                </label>
              ))}
            </div>
          </div>

          {usesApiKey ? (
            <div>
              <label className="label" htmlFor="opf-key">
                API key
              </label>
              <input
                id="opf-key"
                type="password"
                autoComplete="new-password"
                className="input"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={busy}
                required={mode === 'create'}
                placeholder={mode === 'edit' ? 'Deja en blanco para mantener la actual' : undefined}
              />
            </div>
          ) : null}

          {/* Capabilities — what the resource is configured to extract. */}
          <div>
            <p className="label mb-1">Capacidades</p>
            <div className="flex flex-wrap gap-4">
              {OCR_CAPABILITY_OPTIONS.map((c) => (
                <label key={c.value} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={capabilities.includes(c.value)}
                    onChange={() => toggleCapability(c.value)}
                    disabled={busy}
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                  />
                  {c.label}
                </label>
              ))}
            </div>
            {isMistral ? (
              <p className="mt-1 text-xs text-slate-500">
                En Mistral, los «Pares clave/valor» los deriva el LLM, no el OCR nativo. Marca
                «Estructura» para que OCR 4 devuelva los bloques tipados (overlay de estructura).
              </p>
            ) : null}
          </div>

          {/* Mode — how the provider participates. Only Mistral supports Document AI / Batch; DI is OCR → LLM. */}
          {isMistral ? (
            <div>
              <label className="label" htmlFor="opf-mode">
                Modo de funcionamiento
              </label>
              <select
                id="opf-mode"
                className="input"
                value={providerMode}
                onChange={(e) => setProviderMode(e.target.value as OcrMode)}
                disabled={busy}
              >
                {OCR_MODE_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                «OCR → LLM» mantiene la confianza por campo del scoring. «Document AI directo» pide el JSON
                estructurado al propio modelo (opt-in por tipo; máx. 8 páginas, cae a OCR → LLM si falla).
                «Batch» procesa en lote asíncrono (más barato).
              </p>
            </div>
          ) : null}

          {/* Discovery default — at most one provider drives the sample → schema discovery pipeline. */}
          <div>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={isDiscoveryDefault}
                onChange={(e) => setIsDiscoveryDefault(e.target.checked)}
                disabled={busy}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
              />
              <span>
                Usar por defecto en el discovery
                <span className="mt-0.5 block text-xs text-slate-500">
                  El discovery (muestra → esquema) usará este proveedor. Solo uno puede ser el
                  predeterminado; activarlo lo desmarca en los demás. Si la licencia del tenant no lo
                  tiene asignado, el discovery vuelve a Document Intelligence.
                </span>
              </span>
            </label>
          </div>

          {/* Usage limits — monthly ceilings; exceeding them falls a job back to the DI default. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="opf-calls">
                Límite mensual de llamadas
              </label>
              <input
                id="opf-calls"
                type="number"
                min={0}
                className="input"
                placeholder="Sin límite"
                value={monthlyCalls}
                onChange={(e) => setMonthlyCalls(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label className="label" htmlFor="opf-pages">
                Límite mensual de páginas
              </label>
              <input
                id="opf-pages"
                type="number"
                min={0}
                className="input"
                placeholder="Sin límite"
                value={monthlyPages}
                onChange={(e) => setMonthlyPages(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          {/* License gate — only tenants on a listed plan can route OCR to this resource. */}
          <div>
            <p className="label mb-1">Planes asignados</p>
            <div className="flex flex-wrap gap-4">
              {(plans ?? []).map((p) => (
                <label key={p.licenseType} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={assignedLicenses.includes(p.licenseType)}
                    onChange={() => toggleLicense(p.licenseType)}
                    disabled={busy}
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                  />
                  {p.displayName}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'Guardando…' : mode === 'create' ? 'Crear' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}
